import 'react-native-gesture-handler'; // Import before React. iOS/Android requirement.
import 'react-native-url-polyfill/auto';
import 'react-native-get-random-values';
import * as SplashScreen from 'expo-splash-screen';
import { ApplicationProvider, IconRegistry } from '@ui-kitten/components';
import { Config, KalibraDesign, useAppInitializer } from './src/core';
import { Image, LogBox, View, StyleSheet, Text, TextInput, Platform } from 'react-native';
import React, { Suspense, useEffect } from 'react';
import { useIsUserAutoLogin, useUserInitializer, useUserLoginInitializer } from 'src/core/hooks/useUser';

import AppRootView from './src/components/AppRootView';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import ErrorScreen from 'src/screens/error';
import { EvaIconsPack } from '@ui-kitten/eva-icons';
import { InitErrorCode } from 'src/core/types/InitError';
import { KalibraCustomIconPack } from './src/core/constants/KalibraCustomIconPack';
import ThemeInit from 'src/components/shared/ThemeInit';
import { useAfterLoginInitializer } from 'src/core/hooks/useAfterLoginInitializer';
import { useAnalyticsInitializer } from 'src/core/hooks/useAnalyticsInitializer';
import { useAppStore } from 'src/core/store';
import { useInitAppThemeLegacy } from 'src/core/hooks/useAppTheme';
import { useInitDataSharingList } from 'src/core/hooks/tenant/useInitDataSharingList';
import { useInitNotifications } from 'src/core/hooks/useNotifications';
import { useInitTelemetry } from 'src/core/hooks/useTelemetry';
import { useMarkersInitializer } from 'src/core/hooks/useMarkers';
import { useMultiScanInitializer } from 'src/core/hooks/useMultiScan';
import { useNeedUpdateApp } from 'src/core/hooks/useNeedUpdateApp';
import { useNotitficationMessage } from 'src/core/hooks/useNotificationMessage';
import { usePillarCategories } from 'src/core/hooks/usePillarCategories';
import { useSetDefaultServer } from 'src/core/hooks/useSetDefaultServer';
import { useWearableInitializer } from 'src/core/hooks/useWearableSdk';
import { useInitDataSharingRequestList } from 'src/core/hooks/tenant/useInitDataSharingRequestList';
import { useAllMarkerConversionEffect } from 'src/core/hooks/useAllMarkerConversions';
import { fetchAuthSession } from '@aws-amplify/auth';
import LottieView from 'lottie-react-native';
import Spinner from 'react-native-loading-spinner-overlay';
const Brands = require('./brands.json');
const Splash = require('./assets/images/brand/splash.png');

// set FontSizeMultiplier = 1
(Text as any).defaultProps = { ...((Text as any).defaultProps || {}), maxFontSizeMultiplier: 1 };
(TextInput as any).defaultProps = { ...((TextInput as any).defaultProps || {}), maxFontSizeMultiplier: 1 };

if (!Config.DEBUG_MODE) LogBox.ignoreAllLogs();

export default function App(): React.JSX.Element {
  // styles
  const styleContainer = StyleSheet.create({
    splashContainer: {
      width: '100%',
      height: '100%'
    },
    splash: { width: '100%', height: '100%' }
  });
  // call custom initializer hook
  const appPreInit = useAppInitializer();
  const isUserAutoLogin = useIsUserAutoLogin();
  const themeType = useAppStore((state) => state.themeType);
  const setTheme = useAppStore((state) => state.setTheme);
  const [isReady, setIsReady] = React.useState<boolean>(false);

  const [isSplashScreenFinished, setIsSplashScreenFinished] = React.useState<boolean>(
    Platform.OS === 'web' || Brands.use_animated_splash !== 'true'
  );

  const initThemeLegacy = useInitAppThemeLegacy();

  useNeedUpdateApp();

  useSetDefaultServer();

  useUserInitializer();

  useWearableInitializer();

  const isAppInitComplete = useAfterLoginInitializer();
  useMarkersInitializer();

  useNotitficationMessage();

  useAnalyticsInitializer();

  useInitTelemetry();
  useMultiScanInitializer();

  useInitDataSharingList();

  useInitDataSharingRequestList();

  usePillarCategories();

  useAllMarkerConversionEffect();

  const isLoginModuleInit = useUserLoginInitializer();

  useEffect(() => {
    initThemeLegacy(setTheme);
  }, [initThemeLegacy, setTheme]);

  useInitNotifications();

  useEffect(() => {
    const checkLogin = async () => {
      try {
        const session = await fetchAuthSession();
        const isSignedIn = session.tokens?.accessToken != null;
        if (Brands.use_animated_splash == 'true' && Platform.OS != 'web') {
          setIsSplashScreenFinished(isSignedIn);
        }
        setIsReady(true);
        SplashScreen.hideAsync();
      } catch (error) {
        console.error(error);
        setIsReady(true);
        SplashScreen.hideAsync();
      }
    };

    if (appPreInit.isInitComplete) {
      checkLogin();
    }
  }, [appPreInit.isInitComplete, isLoginModuleInit]);

  if (
    appPreInit.initErrorCode !== InitErrorCode.NO_NETWORK_CONNECTION &&
    (appPreInit.initErrorCode !== InitErrorCode.OK ||
      (isAppInitComplete !== null && isAppInitComplete !== InitErrorCode.OK))
  ) {
    return <ErrorScreen code={appPreInit.initErrorCode} />;
  }

  if (!appPreInit.isInitComplete || !isLoginModuleInit || !isReady || isUserAutoLogin) {
    return (
      <View style={styleContainer.splashContainer}>
        {appPreInit.isInitComplete && isLoginModuleInit && isReady && <Spinner visible={true} />}
        <Image style={styleContainer.splash} source={Splash} />
      </View>
    );
  }

  if (!isSplashScreenFinished) {
    if (Platform.OS !== 'web' && Brands.use_animated_splash === 'true') {
      return (
        <Suspense fallback={<Image style={{ width: '100%', height: '100%' }} source={Splash} />}>
          <View
            style={{
              width: '100%',
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
            <LottieView
              style={{ width: Platform.OS === 'ios' && Platform.isPad ? '75%' : '100%', height: '100%' }}
              source={require('assets/images/brand/animated-splash-screen')}
              autoPlay
              renderMode={'AUTOMATIC'}
              hardwareAccelerationAndroid={true}
              loop={false}
              onAnimationFinish={() => {
                setIsSplashScreenFinished(true);
              }}
              resizeMode={'cover'}
            />
          </View>
        </Suspense>
      );
    }
    return (
      <View style={styleContainer.splashContainer}>
        <Image style={styleContainer.splash} source={Splash} />
      </View>
    );
  }

  // render application entry point
  return (
    <>
      <IconRegistry icons={[EvaIconsPack, KalibraCustomIconPack]} />
      <ApplicationProvider {...KalibraDesign} theme={themeType}>
        <BottomSheetModalProvider>
          <ThemeInit />

          <AppRootView />
        </BottomSheetModalProvider>
      </ApplicationProvider>
    </>
  );
}
